import { Controller } from "@hotwired/stimulus";
import debounce from "lodash.debounce";

import axios from "../api/axios";
import toQueryString from "../utils/toQueryString";

export default class extends Controller {
  static targets = ["selectUser", "selectMaterial"];

  static values = {
    urlUser: String,
    urlMaterial: String,
    urlParams: Object,
    items: Array,
  };

  initialize() {
    this.debouncedUserSearch = debounce(this.requestSearchUser.bind(this), 200);
    this.debouncedMaterialSearch = debounce(
      this.requestSearchMaterial.bind(this),
      200
    );
  }

  async connect() {
    if (!this.hasSelectUserTarget || !this.hasSelectMaterialTarget) return;

    const { default: Choices } = await import("choices.js");

    const choiceParams = {
      placeholder: true,
      placeholderValue: "Suche...",
      // searchPlaceholderValue: "Search...",
      searchFloor: 2, // min 2 characters
      searchResultLimit: 30,
      loadingText: "Laden...",
      noResultsText: "Keine Ergebnisse",
      noChoicesText: "Keine Ergebnisse",
      itemSelectText: "Klicke, um auszuwählen",
    };

    this.choicesUser = new Choices(this.selectUserTarget, choiceParams);
    this.choicesMaterial = new Choices(this.selectMaterialTarget, choiceParams);

    // start debounced search
    this.selectUserTarget.addEventListener("search", this.debouncedUserSearch);
    this.selectMaterialTarget.addEventListener(
      "search",
      this.debouncedMaterialSearch
    );

    // clear choices after user selected an item
    this.selectUserTarget.addEventListener("choice", () => {
      this.choicesUser.clearChoices();
    });
    this.selectMaterialTarget.addEventListener("choice", () => {
      this.choicesMaterial.clearChoices();
    });
  }

  async search(key, query) {
    const response = await axios.get(this.buildQueryUrl(key, query));
    if (response.status === 200) {
      return response.data;
    }
    throw new Error(response.toString());
  }

  buildQueryUrl(key, query) {
    const urlParams = key === "material" ? this.urlParamsValue || {} : {};
    urlParams.query = query;

    return `${
      key === "material" ? this.urlMaterialValue : this.urlUserValue
    }${toQueryString(urlParams)}`;
  }

  async requestSearchUser() {
    const query = this.choicesUser.input.value;
    const results = await this.search("user", query);
    this.choicesUser.clearChoices();
    this.choicesUser.setChoices(results, "value", "label", true);
  }

  async requestSearchMaterial() {
    const query = this.choicesMaterial.input.value;
    const results = await this.search("material", query);
    // exclude materials that are already in the collection
    const filteredResults = [];
    results.collection.forEach((material) => {
      if (!this.itemsValue.includes(material.id)) {
        filteredResults.push({ value: material.id, label: material.name });
      }
    });
    this.choicesMaterial.clearChoices();
    this.choicesMaterial.setChoices(filteredResults, "value", "label", true);
  }

  disconnect() {
    if (this.choicesUser) {
      this.choicesUser.destroy();
    }
    if (this.choicesMaterial) {
      this.choicesMaterial.destroy();
    }

    if (this.lookupTimeout) {
      clearTimeout(this.lookupTimeout);
    }
  }

  addUrlParams(event) {
    if (event.target.value === "") {
      this.urlParamsValue = {
        ...this.urlParamsValue,
        [event.params.urlKey]: undefined,
      };
    } else {
      this.urlParamsValue = {
        ...this.urlParamsValue,
        [event.params.urlKey]: event.target.value,
      };
    }
    this.requestSearchMaterial();
  }
}
